import type {
    FileCache,
    FileBrowserItemData,
    FileBrowserItems,
} from '@pyranger/types';
import type { TreeItemIndex } from 'react-complex-tree';

import {
    FILE_BROWSER_ROOT_INDEX,
    FILE_BROWSER_ROOT_ITEM,
} from '@pyranger/constants';

/**
 * Get the parent path of a file or folder path
 * @param path - Path to get parent of, e.g. '#/newfolder/test2.py'
 * @returns Parent path with trailing slash, e.g. '#/newfolder/', or null if at root level
 */
export const getParentPath = (path: string): string | null => {
    const segments = path.split('/');

    // If path has no slashes, it's at root level
    if (segments.length === 1) return null;

    // If path ends with slash, remove empty last segment
    if (path.endsWith('/')) segments.pop();

    // Remove the last segment (file or folder name)
    segments.pop();

    // If we're left with nothing, path was at root
    if (segments.length === 0) return null;

    // Join remaining segments and add trailing slash
    return `${segments.join('/')}/`;
};

/**
 * Options for converting file cache to file browser items.
 */
export interface ConvertFileCacheOptions {
    /** If true, sort folders before files in each directory. Default: true. */
    foldersFirst?: boolean;
}

/**
 * Converts a {@link FileCache} to a {@link FileBrowserItems}. This is necessary when converting the
 * data structure of the files on the device to the data structure expected by the FileBrowser.
 * @param fileCache an object representing the folders and files on the device
 * @param options sort/display options
 * @returns an object that `react-complex-tree` can understand
 */
export const convertFileCacheToFileBrowserItems = (
    fileCache: FileCache | null,
    options: ConvertFileCacheOptions = {},
): FileBrowserItems => {
    const { foldersFirst = true } = options;
    if (fileCache === null) {
        throw new Error(
            'Called convertFileCacheToFileBrowserItems but fileCache was null',
        );
    }

    const result: FileBrowserItems = {};

    const rootChildren: string[] = [];

    Object.entries(fileCache).forEach(([path, file]) => {
        const { size } = file;

        const parts = path.split('/');

        if (parts[parts.length - 1] === '') {
            if (parts.length === 2) {
                rootChildren.push(path);
            }
        } else if (parts.length === 1) {
            rootChildren.push(path);
        }

        const name =
            parts[parts.length - 1] === ''
                ? parts[parts.length - 2]
                : parts[parts.length - 1];

        const item: FileBrowserItemData = {
            size,
            name,
            path,
        };

        result[path] = {
            index: path,
            data: item,
            isFolder: file.type === 'directory',
        };

        Object.keys(fileCache).forEach((key) => {
            if (
                file.type === 'directory' &&
                path !== key &&
                key.includes(path)
            ) {
                if (path === getParentPath(key)) {
                    if (result[path].children === undefined) {
                        result[path].children = [];
                    }
                    result[path].children.push(key);
                }
            }
        });
    });

    // `react-complex-tree` expects there to be a root item, but the fileCache doesn't have any
    // concept of a root item, so we need to add one.
    result[FILE_BROWSER_ROOT_INDEX] = {
        ...FILE_BROWSER_ROOT_ITEM,
        children: sortChildren(rootChildren, result, foldersFirst),
    };

    // Sort all directory children alphabetically
    for (const item of Object.values(result)) {
        if (item.isFolder && item.children) {
            item.children = sortChildren(item.children, result, foldersFirst);
        }
    }

    return result;
};

/**
 * Sort children paths alphabetically. When `foldersFirst` is true, folders
 * are grouped before files. Comparison is case-insensitive on the item name.
 */
function sortChildren(
    children: TreeItemIndex[],
    items: FileBrowserItems,
    foldersFirst: boolean,
): TreeItemIndex[] {
    return [...children].sort((a, b) => {
        const itemA = items[a];
        const itemB = items[b];

        // Folders before files (when enabled)
        if (foldersFirst) {
            if (itemA?.isFolder && !itemB?.isFolder) return -1;
            if (!itemA?.isFolder && itemB?.isFolder) return 1;
        }

        // Alphabetical by name (case-insensitive)
        const nameA = (itemA?.data?.name ?? a).toLowerCase();
        const nameB = (itemB?.data?.name ?? b).toLowerCase();
        return nameA.localeCompare(nameB);
    });
}

/**
 * Gets a string to use as the default folder name, if creating a new folder, or a file name with
 * extension if creating a new file. Follows a preset naming convention, as follows:
 *
 * For folders:
 * - the first new folder will be named "new_folder"
 * - the second new folder will be named "new_folder_2"
 * - the third new folder will be named "new_folder_3"
 * - etc.
 * For files:
 * - the first new file will be named "new_file.py"
 * - the second new file will be named "new_file_2.py"
 * - the third new file will be named "new_file_3.py"
 * - etc.
 * @returns a string to use as the default folder name or file name with extension
 */
export const getDefaultBaseName = ({
    /**
     * True if the item is a folder
     */
    isFolder,
    /**
     * Current items in react-complex-tree
     */
    items,
    /**
     * Index of the last active folder, or null if there is none
     */
    lastActiveFolder,
}: {
    /**
     * The name to use as the default filename, without the extension or any index
     */
    isFolder: boolean;
    /**
     * The current items in react-complex-tree
     */
    items: FileBrowserItems;
    /**
     * The path of the last folder that was selected, or null if there is none
     */
    lastActiveFolder: string | null;
}): string => {
    const namePreset = isFolder ? 'new_folder' : 'new_file';

    const defaultResult = isFolder ? `${namePreset}/` : `${namePreset}.py`;

    let parent = items?.[FILE_BROWSER_ROOT_INDEX] ?? null;

    if (lastActiveFolder !== null) {
        parent = items?.[lastActiveFolder] ?? null;
    }

    if (parent === null) {
        return defaultResult;
    }

    const siblings = parent?.children ?? null;

    if (siblings === null) {
        return defaultResult;
    }

    // Find all siblings that match our naming pattern
    const pattern = new RegExp(
        `${namePreset}(?:_(\\d+))?${!isFolder ? '\\.[^\\.]+' : '\\/?'}$`,
    );

    const existingIndices = siblings
        .map((sibling) => {
            const index = items[sibling]?.index ?? null;

            if (typeof index !== 'string') {
                return NaN;
            }

            // Remove trailing slash for matching if it exists
            const cleanIndex = index.replace(/\/$/, '');
            const match = cleanIndex.match(pattern);
            return match ? parseInt(match[1] || '1') : 0;
        })
        .filter((index) => !isNaN(index));

    // If no matches found, return the preset name without an index
    if (existingIndices.length === 0) {
        return defaultResult;
    }

    // Find the highest index
    const highestIndex = Math.max(...existingIndices);

    // If the highest index is 0, return the preset name without an index
    if (highestIndex === 0) {
        return defaultResult;
    }

    const nextIndex = highestIndex + 1;

    return isFolder
        ? `${namePreset}_${nextIndex}/`
        : `${namePreset}_${nextIndex}.py`;
};
